import { DocumentUploadType, UploadTagType } from 'constants/documents'
import { usePurchaseFlowConfig, usePurchaseFlowProducts, useTargetOrderUser } from '../../_main/contexts'

import { PurchaseFlowEndpoint } from 'constants/API'
import constate from 'constate'
import { getSignedURLRetrieverSeq } from 'components/common/FileAPI/fileUtils'
import { useFileAPI } from 'components/common/FileAPI'
import { useMemo } from 'react'

export const [PowerOfAttorneyStepContextProvider, usePowerOfAttorneyStep] = constate(() => {

  const { sessionId } = usePurchaseFlowConfig()
  const { targetUser } = useTargetOrderUser()
  const { selectedProducts } = usePurchaseFlowProducts()

  // Guards used below: `ConfigSessionShim` fires `getCategoryConfig` on mount
  // so `sessionId` is populated before the user can interact with upload UI in
  // normal flow. These guards handle the edge case where the session hasn't
  // resolved yet (network delay, BE error) — returning a rejected promise
  // transitions the FileAPI chip to FileState.ERROR instead of crashing.
  const getSessionId = (): string | null => sessionId

  const powerOfAttorneyUpload = useFileAPI('PF_POWER_OF_ATTORNEY', {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => {
      const sid = getSessionId()
      if (!sid) return Promise.reject(new Error('PowerOfAttorney upload: sessionId not ready'))
      return api.post(
        PurchaseFlowEndpoint.GET_POA_UPLOAD_URL,
        { sessionId: sid },
        {
          filename: fileEntry.fileObject.name,
          contentType: fileEntry.fileObject.type,
        },
        true
      )
    }),
    deleteHandler: (_, api) => {
      const sid = getSessionId()
      if (!sid) return Promise.reject(new Error('PowerOfAttorney delete: sessionId not ready'))
      return api.delete(
        PurchaseFlowEndpoint.DELETE_UPLOADED_POA,
        { sessionId: sid },
        true
      )
    }
  })

  const powerOfAttorneyRepsUpload = useFileAPI('PF_POWER_OF_ATTORNEY_REPS', {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => {
      const sid = getSessionId()
      if (!sid) return Promise.reject(new Error('PowerOfAttorneyReps upload: sessionId not ready'))
      return api.post(
        PurchaseFlowEndpoint.GET_POA_REPS_UPLOAD_URL,
        { sessionId: sid },
        {
          filename: fileEntry.fileObject.name,
          contentType: fileEntry.fileObject.type,
        },
        true
      )
    }),
    deleteHandler: (_, api) => {
      const sid = getSessionId()
      if (!sid) return Promise.reject(new Error('PowerOfAttorneyReps delete: sessionId not ready'))
      return api.delete(
        PurchaseFlowEndpoint.DELETE_UPLOADED_POA_REPS,
        { sessionId: sid },
        true
      )
    }
  })

  const powerOfAttorneyOtherFilesUpload = useFileAPI('PF_POWER_OF_ATTORNEY_OTHER_FILES', {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => {
      const sid = getSessionId()
      if (!sid) return Promise.reject(new Error('PowerOfAttorneyOther upload: sessionId not ready'))
      return api.post(
        PurchaseFlowEndpoint.GET_DOCUMENT_UPLOAD_URL,
        { sessionId: sid },
        {
          productId: Object.values(selectedProducts)[0].id.toString(),
          documentMetadata: {
            filename: fileEntry.fileObject.name,
            contentType: fileEntry.fileObject.type,
            type: DocumentUploadType.INPUT,
            tag: UploadTagType.ORDER_SUPPLEMENTARY_DOCUMENT,
          }
        },
        true
      )
    }),
    deleteHandler: (_, api) => {
      const sid = getSessionId()
      if (!sid) return Promise.reject(new Error('PowerOfAttorneyOther delete: sessionId not ready'))
      return api.delete(
        PurchaseFlowEndpoint.DELETE_UPLOADED_DOCUMENT,
        { sessionId: sid },
        true
      )
    }
  })

  const isPowerOfAttorneyStepValid = useMemo(() => {
    const isRequestingRepresentative = targetUser.requiresRepresentativePowerOfAttorney
    const isPowerOfAttorneyUploaded = powerOfAttorneyUpload?.allFilesArray.length > 0
    const isPowerOfAttorneyRepsUploaded = powerOfAttorneyRepsUpload?.allFilesArray.length > 0

    if (isRequestingRepresentative && (isPowerOfAttorneyUploaded || isPowerOfAttorneyRepsUploaded)) {
      if (isPowerOfAttorneyUploaded && isPowerOfAttorneyRepsUploaded) {
        return true
      }

      return false
    }

    return true
  }, [powerOfAttorneyRepsUpload?.allFilesArray.length, powerOfAttorneyUpload?.allFilesArray.length, targetUser.requiresRepresentativePowerOfAttorney])

  return {
    powerOfAttorneyUpload,
    powerOfAttorneyRepsUpload,
    isPowerOfAttorneyStepValid,
    powerOfAttorneyOtherFilesUpload,
  }
})
